/**
 * Working out what a typed expression comes to, and drawing it properly.
 *
 * Two jobs that sound unrelated and are not: both exist because the answer a
 * child types has to stay **plain text**. The question bank stores the mark
 * scheme's answer as text, the checker compares text, and a child reading an
 * answer back over the phone reads text. So nothing here produces a document
 * model or LaTeX - the calculator evaluates a string, and the preview draws
 * that same string as maths without changing it.
 *
 * `eval()` is not used and must not be. This runs in a browser on a public
 * site, the string comes from a text box, and a parser is forty lines.
 */

// ── Evaluating ─────────────────────────────────────────────────────────────

const FUNCTIONS = {
  sqrt: Math.sqrt,
  abs: Math.abs,
  ln: Math.log,
  log: (x) => Math.log10(x),
  exp: Math.exp,
}

const TRIG = { sin: Math.sin, cos: Math.cos, tan: Math.tan }
const INVERSE_TRIG = { asin: Math.asin, acos: Math.acos, atan: Math.atan }

/**
 * A recursive-descent parser over the usual precedence.
 *
 *   expression := term (('+' | '-') term)*
 *   term       := unary (('*' | '/') unary)*
 *   unary      := ('-')? power
 *   power      := postfix ('^' unary)?        right associative, as maths is
 *   postfix    := atom ('%' | '!')*
 *   atom       := number | constant | function '(' expression ')' | '(' … ')'
 *
 * Unary minus sits *above* the power, so -3^2 is -(3^2) = -9, which is what
 * the paper means and what a mark scheme expects. Putting it below gives 9,
 * and that is a silent wrong answer rather than an error.
 *
 * Implicit multiplication is accepted - 2(3+4) and 3π both work - because a
 * child typing a sum does not think of × as compulsory, and refusing it makes
 * the calculator feel broken rather than strict.
 */
export function evaluate(input, { degrees = true } = {}) {
  const src = String(input ?? '')
    .replace(/[×]/g, '*')
    .replace(/[÷]/g, '/')
    .replace(/[−–—]/g, '-')
    .replace(/π/g, 'pi')
    .replace(/√/g, 'sqrt')
    .replace(/\s+/g, '')

  let i = 0
  const peek = () => src[i]
  const eof = () => i >= src.length

  const toRad = (x) => (degrees ? (x * Math.PI) / 180 : x)
  const fromRad = (x) => (degrees ? (x * 180) / Math.PI : x)

  function parseExpression() {
    let left = parseTerm()
    while (!eof() && (peek() === '+' || peek() === '-')) {
      const op = src[i++]
      const right = parseTerm()
      left = op === '+' ? left + right : left - right
    }
    return left
  }

  function parseTerm() {
    let left = parseUnary()
    for (;;) {
      if (eof()) break
      const ch = peek()
      if (ch === '*' || ch === '/') {
        i++
        const right = parseUnary()
        if (ch === '/' && right === 0) throw new Error('Cannot divide by zero')
        left = ch === '*' ? left * right : left / right
        continue
      }
      // Implicit multiplication: a number or ) followed by ( or a letter.
      if (ch === '(' || /[a-z]/i.test(ch)) {
        const right = parseUnary()
        left = left * right
        continue
      }
      break
    }
    return left
  }

  function parseUnary() {
    if (!eof() && peek() === '-') { i++; return -parseUnary() }
    if (!eof() && peek() === '+') { i++; return parseUnary() }
    return parsePower()
  }

  function parsePower() {
    const base = parsePostfix()
    if (!eof() && peek() === '^') {
      i++
      // Right associative, and through parseUnary so 2^-1 reads.
      return Math.pow(base, parseUnary())
    }
    return base
  }

  function parsePostfix() {
    let value = parseAtom()
    for (;;) {
      if (!eof() && peek() === '%') { i++; value = value / 100; continue }
      if (!eof() && peek() === '!') {
        i++
        if (value < 0 || !Number.isInteger(value) || value > 170) throw new Error('Cannot do that factorial')
        let f = 1
        for (let n = 2; n <= value; n++) f *= n
        value = f
        continue
      }
      break
    }
    return value
  }

  function parseAtom() {
    if (eof()) throw new Error('The sum is not finished')

    if (peek() === '(') {
      i++
      const v = parseExpression()
      if (peek() !== ')') throw new Error('A bracket is not closed')
      i++
      return v
    }

    const number = /^\d*\.?\d+/.exec(src.slice(i))
    if (number) { i += number[0].length; return Number(number[0]) }

    const word = /^[a-z]+/i.exec(src.slice(i))
    if (word) {
      const name = word[0].toLowerCase()
      if (name === 'pi') { i += 2; return Math.PI }
      if (name === 'e') { i += 1; return Math.E }

      i += name.length
      if (peek() !== '(') throw new Error(`${name} needs a bracket after it`)
      i++
      const arg = parseExpression()
      if (peek() !== ')') throw new Error('A bracket is not closed')
      i++

      if (name in TRIG) return TRIG[name](toRad(arg))
      if (name in INVERSE_TRIG) return fromRad(INVERSE_TRIG[name](arg))
      if (name in FUNCTIONS) {
        if (name === 'sqrt' && arg < 0) throw new Error('Cannot square-root a negative')
        if ((name === 'log' || name === 'ln') && arg <= 0) throw new Error('Cannot take the log of that')
        return FUNCTIONS[name](arg)
      }
      throw new Error(`I do not know "${name}"`)
    }

    throw new Error(`I cannot read "${src[i]}"`)
  }

  const value = parseExpression()
  if (!eof()) throw new Error(`I cannot read "${src.slice(i)}"`)
  if (!Number.isFinite(value)) throw new Error('That does not come to a number')
  return value
}

/**
 * A result a person would write down.
 *
 * Floating point gives 0.30000000000000004 for 0.1 + 0.2, and a calculator
 * that prints that is a calculator nobody trusts. Twelve significant figures
 * is past anything GCSE asks for and short of where the error shows.
 */
export function formatResult(value) {
  if (!Number.isFinite(value)) return '—'
  if (Number.isInteger(value) && Math.abs(value) < 1e15) return String(value)
  const rounded = Number(value.toPrecision(12))
  if (Math.abs(rounded) >= 1e10 || (Math.abs(rounded) < 1e-6 && rounded !== 0)) {
    return rounded.toExponential(6).replace(/e([+-])(\d+)/, ' × 10^$1$2')
  }
  return String(rounded)
}

// ── Drawing ────────────────────────────────────────────────────────────────

/**
 * The typed text, split into pieces a React component can lay out as maths.
 *
 * Not a full parser and deliberately so - it recognises the three shapes a
 * GCSE answer is actually written in and leaves everything else as plain
 * text. A preview that silently restructures what a child typed would be
 * worse than no preview: they would stop believing the box.
 *
 * Returns a flat list of {type, ...} in reading order:
 *   {type:'text', value}
 *   {type:'frac', top, bottom}
 *   {type:'sup',  base, exp}
 *   {type:'root', value}
 */
export function layoutMaths(input) {
  const s = String(input ?? '')
  if (!s.trim()) return []

  const out = []
  let buffer = ''
  const flush = () => { if (buffer) { out.push({ type: 'text', value: buffer }); buffer = '' } }

  let i = 0
  while (i < s.length) {
    const rest = s.slice(i)

    // sqrt(...) or √(...) or √9
    const root = /^(?:sqrt|√)\s*(?:\(([^()]*)\)|(\d+(?:\.\d+)?))/i.exec(rest)
    if (root) {
      flush()
      out.push({ type: 'root', value: root[1] ?? root[2] })
      i += root[0].length
      continue
    }

    // a/b where both sides are plain - 3/8, 2x/5, (a+b)/2
    const frac = /^(\([^()]*\)|[0-9a-z.]+)\s*\/\s*(\([^()]*\)|[0-9a-z.]+)/i.exec(rest)
    if (frac && !buffer.endsWith('^')) {
      // Do not steal the number in front of the slash from a word already
      // being built - "x/2" is a fraction, "sin/2" is not something to draw.
      const stripped = /[0-9a-z.]+$/i.exec(buffer)
      if (stripped) buffer = buffer.slice(0, buffer.length - stripped[0].length)
      flush()
      out.push({
        type: 'frac',
        top: (stripped ? stripped[0] : '') + frac[1],
        bottom: frac[2],
      })
      i += frac[0].length
      continue
    }

    // x^2 or x^{10}
    const sup = /^\^\s*(?:\{([^{}]*)\}|(-?[0-9a-z.]+))/i.exec(rest)
    if (sup && buffer) {
      const base = /[0-9a-z.)]+$/i.exec(buffer)
      const baseText = base ? base[0] : ''
      buffer = buffer.slice(0, buffer.length - baseText.length)
      flush()
      out.push({ type: 'sup', base: baseText, exp: sup[1] ?? sup[2] })
      i += sup[0].length
      continue
    }

    buffer += s[i]
    i++
  }
  flush()
  return out
}
